import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SectionHeading } from "@/components/Atoms";
import { SquadPicker } from "@/components/SquadPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChallengeExpiry } from "@/components/ChallengeExpiry";
import { Bot, Swords, Trophy, Skull, Coins, Search, User as UserIcon, Hourglass, X, Tv, Users, Plus, Eye, Crown, TrendingUp, Flame, ChevronDown, History, TriangleAlert } from "lucide-react";
import { ClosedLoopNotice } from "@/components/ClosedLoopNotice";

function ChallengeDialog({ busy, onConfirm, onClose }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const { data: results } = useQuery({
    queryKey: ["user-search", q],
    queryFn: async () => (await api.get(`/users/search?q=${encodeURIComponent(q)}`)).data,
    enabled: q.length >= 2 && !sel,
  });
  const headerSlot = (
    <div>
      <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Opponent</Label>
      <div className="relative mt-1">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
        <Input
          data-testid="challenge-opponent-input"
          placeholder="Search collector by name…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setSel(null); }}
          className="pl-9 bg-[hsl(220_18%_12%)] border-white/10"
        />
      </div>
      {results?.length > 0 && !sel && (
        <div className="mt-1 rounded-lg border border-white/10 bg-[hsl(220_18%_10%)] max-h-32 overflow-y-auto">
          {results.map((u) => (
            <button key={u.id} type="button" data-testid={`challenge-opponent-suggestion-${u.id}`}
              onClick={() => { setSel(u); setQ(u.display_name); }}
              className="w-full text-left px-3 py-2 hover:bg-white/5 text-white text-sm flex items-center gap-2">
              <UserIcon className="h-3.5 w-3.5 text-white/40" /> {u.display_name}
            </button>
          ))}
        </div>
      )}
      {sel && <div className="text-emerald-300 text-xs mt-1">✓ Challenging {sel.display_name}</div>}
    </div>
  );
  return (
    <SquadPicker
      title="CHALLENGE A PLAYER"
      confirmLabel="Send Challenge"
      showWager
      showStake
      feeNote="Both players commit the Challenge Entry. Platform Fee is 15% of the pool — the winner takes the rest, plus any cards the loser committed."
      busy={busy}
      headerSlot={headerSlot}
      confirmExtraDisabled={!sel}
      onConfirm={({ cardIds, wager, stakeCardIds }) => onConfirm({ cardIds, wager, stakeCardIds, toUserId: sel.id })}
      onClose={onClose}
    />
  );
}

function BattleRow({ b, children }) {
  return (
    <div className="hud-pill rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`battle-row-${b.id}`}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${b.mode === "pvc" ? "bg-violet-400/15 border border-violet-300/30" : "bg-cyan-400/15 border border-cyan-300/30"}`}>
        {b.mode === "pvc" ? <Bot className="h-4 w-4 text-violet-300" /> : <Swords className="h-4 w-4 text-cyan-300" />}
      </div>
      <div className="min-w-0">
        <div className="text-white text-sm truncate">vs {b.opponent_name}</div>
        <div className="font-mono text-[10px] text-white/40 flex items-center gap-1">
          <Coins className="h-3 w-3 text-amber-300" /> {b.wager.toLocaleString()} wager
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2">{children}</div>
    </div>
  );
}

export default function ArenaPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [preselectId, setPreselectId] = useState(null);
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const [pvcOpen, setPvcOpen] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [openChallengeOpen, setOpenChallengeOpen] = useState(false);
  const [joinFor, setJoinFor] = useState(null);
  const [acceptFor, setAcceptFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const { data: stats } = useQuery({
    queryKey: ["arena-stats"],
    queryFn: async () => (await api.get("/arena/stats/me")).data,
    refetchInterval: 10000,
  });
  const { data: battles } = useQuery({
    queryKey: ["arena-battles"],
    queryFn: async () => (await api.get("/arena/battles")).data,
    refetchInterval: 5000,
  });
  const { data: buffs, refetch: rerollBuffs } = useQuery({
    queryKey: ["arena-buffs"],
    queryFn: async () => (await api.get("/arena/buffs")).data,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const { data: difficulties } = useQuery({
    queryKey: ["arena-difficulties"],
    queryFn: async () => (await api.get("/arena/difficulties")).data,
  });
  const { data: spectator } = useQuery({
    queryKey: ["spectator-matches"],
    queryFn: async () => (await api.get("/arena/spectator/matches")).data,
    refetchInterval: 3000,
  });
  const { data: leaderboard } = useQuery({
    queryKey: ["arena-leaderboard"],
    queryFn: async () => (await api.get("/arena/leaderboard")).data,
    refetchInterval: 15000,
  });
  const { data: openLobby } = useQuery({
    queryKey: ["arena-open-lobby"],
    queryFn: async () => (await api.get("/arena/battles/open")).data,
    refetchInterval: 5000,
  });
  const [startingMatch, setStartingMatch] = useState(false);

  useEffect(() => {
    const pre = searchParams.get("preselect");
    if (pre) {
      setPreselectId(pre);
      setPvcOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("preselect");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const startSpectatorMatch = async () => {
    setStartingMatch(true);
    try {
      const r = await api.post("/arena/spectator/matches");
      qc.invalidateQueries({ queryKey: ["spectator-matches"] });
      navigate(`/arena/watch/${r.data.id}`);
    } catch (e) { toast.error(apiError(e, "Couldn't start a match")); }
    finally { setStartingMatch(false); }
  };

  const startPvc = async ({ cardIds, wager, buffPicks, difficulty, useToken }) => {
    setBusy(true);
    try {
      const r = await api.post("/arena/battles/pvc", {
        card_ids: cardIds,
        wager: useToken ? 0 : wager,
        buffs: useToken ? [] : (buffPicks || []),
        difficulty,
        use_token: !!useToken,
      });
      refresh();
      navigate(`/arena/battle/${r.data.id}`);
    } catch (e) { toast.error(apiError(e, "Failed to start battle")); }
    finally { setBusy(false); }
  };

  const sendChallenge = async ({ cardIds, wager, toUserId, stakeCardIds }) => {
    setBusy(true);
    try {
      await api.post("/arena/battles/challenge", { to_user_id: toUserId, card_ids: cardIds, wager, stake_card_ids: stakeCardIds || [] });
      refresh();
      setChallengeOpen(false);
      qc.invalidateQueries({ queryKey: ["arena-battles"] });
      toast.success("Challenge sent! Waiting for your opponent…");
    } catch (e) { toast.error(apiError(e, "Failed to send challenge")); }
    finally { setBusy(false); }
  };

  const acceptChallenge = async ({ cardIds, stakeCardIds }) => {
    setBusy(true);
    try {
      const r = await api.post(`/arena/battles/${acceptFor.id}/accept`, { card_ids: cardIds, stake_card_ids: stakeCardIds || [] });
      refresh();
      setAcceptFor(null);
      navigate(`/arena/battle/${r.data.id}`);
    } catch (e) { toast.error(apiError(e, "Failed to accept")); }
    finally { setBusy(false); }
  };

  const declineOrCancel = async (b, action) => {
    try {
      await api.post(`/arena/battles/${b.id}/${action}`);
      qc.invalidateQueries({ queryKey: ["arena-battles"] });
      qc.invalidateQueries({ queryKey: ["arena-open-lobby"] });
      refresh();
      toast.success(action === "decline" ? "Challenge declined" : "Challenge cancelled — wager refunded");
    } catch (e) { toast.error(apiError(e, "Failed")); }
  };

  const postOpenChallenge = async ({ cardIds, wager, stakeCardIds, maxPowerPct }) => {
    setBusy(true);
    try {
      await api.post("/arena/battles/open", { card_ids: cardIds, wager, stake_card_ids: stakeCardIds || [], max_power_pct: maxPowerPct ?? null });
      refresh();
      setOpenChallengeOpen(false);
      qc.invalidateQueries({ queryKey: ["arena-battles"] });
      qc.invalidateQueries({ queryKey: ["arena-open-lobby"] });
      toast.success("Open challenge posted to the lobby! Anyone can join.");
    } catch (e) { toast.error(apiError(e, "Failed to post challenge")); }
    finally { setBusy(false); }
  };

  const joinChallenge = async ({ cardIds, stakeCardIds }) => {
    setBusy(true);
    try {
      const r = await api.post(`/arena/battles/${joinFor.id}/join`, { card_ids: cardIds, stake_card_ids: stakeCardIds || [] });
      refresh();
      setJoinFor(null);
      navigate(`/arena/battle/${r.data.id}`);
    } catch (e) { toast.error(apiError(e, "Couldn't join — it may have been taken")); }
    finally { setBusy(false); }
  };

  const incoming = battles?.incoming || [];
  const outgoing = battles?.outgoing || [];
  const active = battles?.active || [];
  const history = battles?.history || [];

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-5 sm:py-10">
      <SectionHeading
        eyebrow="Combat"
        title="VS ARENA"
        description="Your cards are your soldiers. Build a squad of 3, take turns, and crush your opponent."
        right={
          <div className="hud-pill rounded-xl px-4 py-2 text-center" data-testid="arena-record">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">Record</div>
            <div className="font-display text-xl text-white mt-0.5">
              <span className="text-emerald-300">{stats?.wins ?? 0}W</span>
              <span className="text-white/30 mx-1">·</span>
              <span className="text-red-400">{stats?.losses ?? 0}L</span>
            </div>
          </div>
        }
      />

      <ClosedLoopNotice className="mb-4 sm:mb-6" testid="arena-closed-loop" />

      <div data-testid="arena-dot-disclaimer" className="mb-6 sm:mb-8 flex items-start gap-2 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] px-3 py-2.5">
        <TriangleAlert className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
        <p className="text-[12px] sm:text-sm text-amber-200/85 leading-snug">
          <b className="text-amber-200">DoT Power is disabled in the Arena.</b> A card's Damage-over-Time stat still shows on the card and works in <span className="text-amber-100">Cavern party delves</span> &amp; races, but it's turned off in 1v1 duels — it would be overpowered head-to-head.
        </p>
      </div>


      {/* Mode select — condensed on mobile so all three fit above the fold */}
      <div className="grid sm:grid-cols-3 gap-2.5 sm:gap-4 mb-6 sm:mb-10">
        <button
          data-testid="arena-pvc-button"
          onClick={() => setPvcOpen(true)}
          className="hud-pill rounded-xl sm:rounded-2xl p-3.5 sm:p-6 text-left group hover:border-violet-400/40 transition-colors duration-200 relative overflow-hidden"
        >
          <div className="absolute inset-0 holo-scan opacity-30" />
          <Bot className="h-5 w-5 sm:h-8 sm:w-8 text-violet-300" />
          <div className="font-display text-base sm:text-2xl text-white tracking-[0.06em] sm:tracking-[0.08em] mt-1.5 sm:mt-3">VS THE MACHINE</div>
          <p className="text-[12.5px] sm:text-sm text-white/55 mt-1 leading-snug">Battle a <span className="text-violet-300">ruthless</span> AI squad. Pick a difficulty, stack modifiers — a losing Challenge Entry costs you a soldier.</p>
        </button>
        <button
          data-testid="arena-pvp-button"
          onClick={() => setChallengeOpen(true)}
          className="hud-pill rounded-xl sm:rounded-2xl p-3.5 sm:p-6 text-left group hover:border-cyan-400/40 transition-colors duration-200 relative overflow-hidden"
        >
          <div className="absolute inset-0 holo-scan opacity-30" />
          <Swords className="h-5 w-5 sm:h-8 sm:w-8 text-cyan-300" />
          <div className="font-display text-base sm:text-2xl text-white tracking-[0.06em] sm:tracking-[0.08em] mt-1.5 sm:mt-3">CHALLENGE A PLAYER</div>
          <p className="text-[12.5px] sm:text-sm text-white/55 mt-1 leading-snug">Call out a specific collector. Commit credits <span className="text-amber-300">and cards</span> — winner keeps the loser's commitments.</p>
        </button>
        <button
          data-testid="arena-open-button"
          onClick={() => setOpenChallengeOpen(true)}
          className="hud-pill rounded-xl sm:rounded-2xl p-3.5 sm:p-6 text-left group hover:border-emerald-400/40 transition-colors duration-200 relative overflow-hidden"
        >
          <div className="absolute inset-0 holo-scan opacity-30" />
          <Users className="h-5 w-5 sm:h-8 sm:w-8 text-emerald-300" />
          <div className="font-display text-base sm:text-2xl text-white tracking-[0.06em] sm:tracking-[0.08em] mt-1.5 sm:mt-3">OPEN CHALLENGE</div>
          <p className="text-[12.5px] sm:text-sm text-white/55 mt-1 leading-snug">Post a squad to the <span className="text-emerald-300">open lobby</span>. Anyone can join — first to accept starts the fight.</p>
        </button>
      </div>

      {/* Open lobby */}
      <div className="mb-6 sm:mb-10" data-testid="open-lobby-section">
        <h2 className="font-display text-xl text-white tracking-[0.1em] flex items-center gap-2 mb-3"><Users className="h-5 w-5 text-emerald-300" /> OPEN LOBBY <span className="text-emerald-300 font-mono text-sm">({(openLobby?.open || []).length})</span></h2>
        {(openLobby?.open || []).length === 0 ? (
          <div className="hud-pill rounded-xl p-6 text-center text-white/40 text-sm">No open challenges right now — post one above for anyone to join.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {openLobby.open.map((c) => (
              <div key={c.id} data-testid={`open-challenge-${c.id}`} className="hud-pill rounded-xl p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-400/15 border border-emerald-300/30 flex items-center justify-center shrink-0">
                  <Swords className="h-5 w-5 text-emerald-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm truncate">{c.challenger_name}</div>
                  <ChallengeExpiry expiresAt={c.expires_at} testId={`challenge-expiry-${c.id}`} />
                  <div className="font-mono text-[10px] text-white/45 flex items-center flex-wrap gap-x-2">
                    <span className="inline-flex items-center gap-1 text-amber-300"><Flame className="h-3 w-3" /> {c.challenger_power?.toLocaleString()} PWR</span>
                    <span className="inline-flex items-center gap-1"><Coins className="h-3 w-3 text-amber-300" /> {c.wager.toLocaleString()}c</span>
                    {c.stake_count > 0 && <span className="text-violet-300">{c.stake_count} card stake</span>}
                    {c.max_power_pct != null && <span className="inline-flex items-center gap-1 text-cyan-300" data-testid={`open-challenge-cap-${c.id}`}>≤ {Math.round((c.challenger_power || 0) * (1 + c.max_power_pct / 100)).toLocaleString()} PWR</span>}
                  </div>
                </div>
                <Button size="sm" data-testid={`join-challenge-${c.id}`} onClick={() => setJoinFor(c)} className="bg-emerald-400 text-black hover:bg-emerald-300">
                  <Swords className="h-4 w-4 mr-1" /> Join
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Community Prediction Pool — spectator */}
      <div className="mb-6 sm:mb-10" data-testid="spectator-section">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h2 className="font-display text-xl text-white tracking-[0.1em] flex items-center gap-2"><Tv className="h-5 w-5 text-amber-300" /> COMMUNITY PREDICTION POOL</h2>
          <Button size="sm" data-testid="start-spectator-match" disabled={startingMatch} onClick={startSpectatorMatch} className="bg-amber-400 text-black hover:bg-amber-300">
            <Plus className="h-4 w-4 mr-1" /> {startingMatch ? "Spinning up…" : "Start a Match"}
          </Button>
        </div>
        <p className="text-sm text-white/50 mb-3">The Machine pits two random squads against each other. Commit credits to a side during the prediction window — the winning side splits the pool (10% entry fee · Platform Fee 35% of the profit).</p>
        {(spectator?.live || []).length === 0 ? (
          <div className="hud-pill rounded-xl p-6 text-center text-white/40 text-sm">No live matches — start one to open predictions.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {spectator.live.map((m) => (
              <button key={m.id} data-testid={`spectator-match-${m.id}`} onClick={() => navigate(`/arena/watch/${m.id}`)} className="hud-pill rounded-xl p-3 text-left hover:border-amber-300/40 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${m.status === "betting" ? "bg-emerald-400/20 text-emerald-300" : "bg-amber-400/20 text-amber-300 animate-pulse"}`}>{m.status === "betting" ? "PREDICTIONS OPEN" : "● LIVE"}</span>
                  <span className="font-mono text-[10px] text-amber-300 inline-flex items-center gap-1"><Coins className="h-3 w-3" /> {m.total_pool.toLocaleString()} pool</span>
                </div>
                <div className="flex items-center gap-2">
                  {m.sides.map((s, i) => (
                    <div key={s.side ?? s.name} className="flex-1 min-w-0">
                      <div className="font-display text-sm truncate" style={{ color: s.color }}>{s.name}</div>
                      <div className="font-mono text-[9px] text-white/40">{s.alive}/3 alive · {s.pool.toLocaleString()}c</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-mono text-[9px] text-white/40 inline-flex items-center gap-1"><Eye className="h-3 w-3" /> watch &amp; predict</span>
                  {m.i_bet && <span className="font-mono text-[9px] text-cyan-300">✓ You're in</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Leaderboard */}
      <div className="mb-6 sm:mb-10" data-testid="leaderboard-section">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4">
          <div>
            <h2 className="font-display text-xl text-white tracking-[0.1em] flex items-center gap-2 mb-3"><Crown className="h-5 w-5 text-amber-300" /> TOP WARRIORS</h2>
            <div className="hud-pill rounded-xl overflow-hidden">
              {(leaderboard?.top || []).length === 0 ? (
                <div className="p-6 text-center text-white/40 text-sm">No victories logged yet — be the first to win.</div>
              ) : (
                (leaderboard.top).map((r) => (
                  <div key={r.user_id} data-testid={`leaderboard-row-${r.rank}`}
                    className={`flex items-center gap-3 px-4 py-2.5 border-b border-white/5 last:border-0 ${r.is_me ? "bg-cyan-400/10" : ""}`}>
                    <span className={`font-display text-lg w-7 text-center ${r.rank === 1 ? "text-amber-300" : r.rank === 2 ? "text-white/70" : r.rank === 3 ? "text-orange-300" : "text-white/40"}`}>{r.rank}</span>
                    <span className="text-white text-sm flex-1 truncate">{r.name}{r.is_me && <span className="text-cyan-300 font-mono text-[10px] ml-1">(you)</span>}</span>
                    <span className="font-mono text-[11px] text-white/50">{r.win_rate}% WR</span>
                    <span className="font-mono text-[11px] text-emerald-300 inline-flex items-center gap-1 w-16 justify-end"><Trophy className="h-3 w-3" /> {r.wins}</span>
                    <span className="font-mono text-[11px] text-red-400/70 w-10 text-right">{r.losses}L</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl text-white tracking-[0.1em] flex items-center gap-2 mb-3"><TrendingUp className="h-5 w-5 text-emerald-300" /> BIGGEST PAYOUTS</h2>
            <div className="hud-pill rounded-xl overflow-hidden">
              {(leaderboard?.biggest_payouts || []).length === 0 ? (
                <div className="p-6 text-center text-white/40 text-sm">No spectator payouts yet — back a winning squad to land here.</div>
              ) : (
                (leaderboard.biggest_payouts).map((p, i) => (
                  <div key={p.id} data-testid={`payout-row-${i}`} className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 last:border-0">
                    <Coins className="h-4 w-4 text-amber-300 shrink-0" />
                    <span className="text-white text-sm flex-1 truncate">{p.name}</span>
                    <span className="font-mono text-[11px] text-emerald-300">+{p.profit.toLocaleString()}c profit</span>
                    <span className="font-mono text-[11px] text-amber-300 w-20 text-right">{p.payout.toLocaleString()}c</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Incoming challenges */}
      {incoming.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-xl text-white tracking-[0.1em] mb-3">INCOMING CHALLENGES <span className="text-cyan-300">({incoming.length})</span></h2>
          <div className="space-y-2">
            {incoming.map((b) => (
              <BattleRow key={b.id} b={b}>
                <Button size="sm" data-testid={`accept-challenge-${b.id}`} onClick={() => setAcceptFor(b)} className="bg-emerald-400 text-black hover:bg-emerald-300">Accept</Button>
                <Button size="sm" variant="outline" data-testid={`decline-challenge-${b.id}`} onClick={() => declineOrCancel(b, "decline")} className="border-white/15 text-white"><X className="h-4 w-4 mr-1" /> Decline</Button>
              </BattleRow>
            ))}
          </div>
        </div>
      )}

      {/* Active battles */}
      <div className="mb-8">
        <h2 className="font-display text-xl text-white tracking-[0.1em] mb-3">ACTIVE BATTLES</h2>
        {active.length === 0 ? (
          <div className="hud-pill rounded-xl p-6 text-center text-white/40 text-sm">No active battles — start one above.</div>
        ) : (
          <div className="space-y-2">
            {active.map((b) => (
              <BattleRow key={b.id} b={b}>
                {b.my_turn && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-black bg-amber-300 rounded-full px-2 py-1 animate-pulse" data-testid={`your-turn-${b.id}`}>Your turn!</span>
                )}
                <Button size="sm" data-testid={`enter-battle-${b.id}`} onClick={() => navigate(`/arena/battle/${b.id}`)} className="bg-cyan-400 text-black hover:bg-cyan-300"><Swords className="h-4 w-4 mr-1" /> Enter</Button>
              </BattleRow>
            ))}
          </div>
        )}
      </div>

      {/* Outgoing pending */}
      {outgoing.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-xl text-white tracking-[0.1em] mb-3">SENT CHALLENGES</h2>
          <div className="space-y-2">
            {outgoing.map((b) => (
              <BattleRow key={b.id} b={b}>
                {b.is_open ? (
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300 inline-flex items-center gap-1" data-testid={`open-waiting-${b.id}`}><Users className="h-3 w-3" /> In open lobby</span>
                ) : (
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 inline-flex items-center gap-1"><Hourglass className="h-3 w-3" /> Waiting</span>
                )}
                <Button size="sm" variant="outline" data-testid={`cancel-challenge-${b.id}`} onClick={() => declineOrCancel(b, "cancel")} className="border-white/15 text-white">Cancel</Button>
              </BattleRow>
            ))}
          </div>
        </div>
      )}

      {/* History (collapsible dropdown) */}
      <div data-testid="battle-history-section">
        <button
          data-testid="battle-history-toggle"
          onClick={() => setHistoryOpen((o) => !o)}
          className="w-full hud-pill rounded-xl px-4 py-3 flex items-center gap-2 hover:border-white/25 transition-colors"
        >
          <History className="h-4 w-4 text-cyan-300" />
          <span className="font-display text-xl text-white tracking-[0.1em]">BATTLE HISTORY</span>
          <span className="font-mono text-sm text-white/40">({history.length})</span>
          <ChevronDown className={`h-5 w-5 text-white/50 ml-auto transition-transform duration-200 ${historyOpen ? "rotate-180" : ""}`} />
        </button>
        {historyOpen && (
          <div className="mt-3" data-testid="battle-history-list">
            {history.length === 0 ? (
              <div className="hud-pill rounded-xl p-6 text-center text-white/40 text-sm">No battles fought yet.</div>
            ) : (
              <div className="space-y-2">
                {history.map((b) => (
                  <BattleRow key={b.id} b={b}>
                    {b.status === "finished" ? (
                      b.i_won ? (
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] text-emerald-300"><Trophy className="h-4 w-4" /> WON {b.reward > 0 ? `+${b.reward.toLocaleString()}c` : ""}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] text-red-400"><Skull className="h-4 w-4" /> LOST</span>
                      )
                    ) : (
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{b.status}</span>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/arena/battle/${b.id}`)} className="text-white/50 hover:text-white">View</Button>
                  </BattleRow>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {pvcOpen && (
        <SquadPicker
          title="VS THE MACHINE"
          confirmLabel="Start Battle"
          showWager
          showToken
          showBuffs
          buffsAvailable={buffs || []}
          onRerollBuffs={rerollBuffs}
          showDifficulty
          difficulties={difficulties || []}
          warnBareGear
          warning={{
            title: "REAL CONSEQUENCES — a losing Challenge Entry costs you a card.",
            body: "WIN: you'll get to spend your Challenge Entry on loot — DRAFT your own rewards (cards, packs or tokens at HALF store price) on the victory screen. Anything you don't pick is filled with RANDOM loot, so none of it is wasted. LOSE: you forfeit your Challenge Entry AND must permanently surrender one of the three soldiers that fought. Set your Challenge Entry to 0 to train risk-free.",
          }}
          feeNote="The Machine is rarity-matched and scales with difficulty. Boosts raise your Challenge Entry; handicaps lower it. You choose your loot after you win."
          busy={busy}
          onConfirm={startPvc}
          onClose={() => { setPvcOpen(false); setPreselectId(null); }}
          preselectId={preselectId}
        />
      )}
      {challengeOpen && <ChallengeDialog busy={busy} onConfirm={sendChallenge} onClose={() => setChallengeOpen(false)} />}
      {openChallengeOpen && (
        <SquadPicker
          title="POST OPEN CHALLENGE"
          confirmLabel="Post to Lobby"
          showWager
          showStake
          showPowerCap
          feeNote="Anyone can join. Both sides commit the same Challenge Entry (and matching cards). Platform Fee is 15% of the pool — the winner keeps the loser's committed cards."
          busy={busy}
          onConfirm={postOpenChallenge}
          onClose={() => setOpenChallengeOpen(false)}
        />
      )}
      {joinFor && (
        <SquadPicker
          title={`JOIN — vs ${joinFor.challenger_name}`}
          confirmLabel="Join & Fight"
          showWager
          wagerLocked={joinFor.wager}
          showStake={(joinFor.stake_count || 0) > 0}
          stakeRequired={joinFor.stake_count || 0}
          powerCap={joinFor.max_power_pct != null ? { pct: joinFor.max_power_pct, hostPower: joinFor.challenger_power || 0, maxPower: Math.round((joinFor.challenger_power || 0) * (1 + joinFor.max_power_pct / 100)) } : null}
          feeNote="You match the challenger's Challenge Entry (and card commitment). Winner takes 85% of the pool plus the loser's committed cards."
          busy={busy}
          onConfirm={joinChallenge}
          onClose={() => setJoinFor(null)}
        />
      )}
      {acceptFor && (
        <SquadPicker
          title={`ACCEPT — vs ${acceptFor.opponent_name}`}
          confirmLabel="Accept & Fight"
          showWager
          wagerLocked={acceptFor.wager}
          showStake={(acceptFor.cards_staked || 0) > 0}
          stakeRequired={acceptFor.cards_staked || 0}
          feeNote="You commit the same Challenge Entry (and matching cards). Winner takes 85% of the pool plus the loser's committed cards."
          busy={busy}
          onConfirm={acceptChallenge}
          onClose={() => setAcceptFor(null)}
        />
      )}
    </div>
  );
}
